import React, { useState, useRef, useEffect } from 'react';
import './SearchableSelect.css';

export interface SearchableSelectOption {
	value: string;
	label: string;
}

interface SearchableSelectProps {
	options: SearchableSelectOption[];
	value: string | null;
	onChange: (value: string | null) => void;
	placeholder?: string;
	label: string;
	id?: string;
}

const ALL_VALUE = '__all__';

export default function SearchableSelect({
	options,
	value,
	onChange,
	placeholder = 'All',
	label,
	id: idProp,
}: SearchableSelectProps) {
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState('');
	const containerRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const id = idProp ?? `searchable-select-${label.replace(/\s+/g, '-').toLowerCase()}`;

	const selectedOption = value === null ? null : options.find(o => o.value === value);
	const displayLabel = selectedOption ? selectedOption.label : placeholder;

	const filteredOptions = search.trim()
		? options.filter(
			o => o.label.toLowerCase().includes(search.toLowerCase())
		)
		: options;

	useEffect(() => {
		if (!open) setSearch('');
	}, [open]);

	useEffect(() => {
		if (open) inputRef.current?.focus();
	}, [open]);

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
				setOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	const handleSelect = (optionValue: string) => {
		onChange(optionValue === ALL_VALUE ? null : optionValue);
		setOpen(false);
	};

	return (
		<div className="searchableSelect" ref={containerRef}>
			<label htmlFor={id} className="searchableSelectLabel">{label}</label>
			<button
				type="button"
				id={id}
				className="searchableSelectTrigger"
				onClick={() => setOpen(o => !o)}
				aria-expanded={open}
				aria-haspopup="listbox"
				aria-label={label}
			>
				{displayLabel}
			</button>
			{open && (
				<div className="searchableSelectDropdown" role="listbox">
					<input
						ref={inputRef}
						type="text"
						className="searchableSelectInput"
						placeholder="Search..."
						value={search}
						onChange={e => setSearch(e.target.value)}
						onKeyDown={e => {
							if (e.key === 'Escape') setOpen(false);
						}}
						aria-label={`Search ${label}`}
					/>
					<ul className="searchableSelectList">
						<li>
							<button
								type="button"
								className={`searchableSelectOption ${value === null ? 'selected' : ''}`}
								onClick={() => handleSelect(ALL_VALUE)}
							>
								{placeholder}
							</button>
						</li>
						{filteredOptions.map(opt => (
							<li key={opt.value}>
								<button
									type="button"
									className={`searchableSelectOption ${value === opt.value ? 'selected' : ''}`}
									onClick={() => handleSelect(opt.value)}
								>
									{opt.label}
								</button>
							</li>
						))}
						{filteredOptions.length === 0 && (
							<li className="searchableSelectEmpty">No matches</li>
						)}
					</ul>
				</div>
			)}
		</div>
	);
}

